import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import { components as clientComponents } from 'mage-client';
import { debounce, isEqual, get, set } from 'lodash';
import { formUtils } from 'moet-core';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { withResponsive } from 'mage-components';

import { CAPS } from '@/constants';
import { getCountries, getUsStates } from '@/selectors/options';
import { isUnmanagedKyc } from '@/selectors/widget';
import { cleanupAddresify } from '@/actions/addresify';
import { getMerchantAwareTranslator, merchantRegion } from '@/selectors/merchant';
import {
  cityField,
  countryField,
  postalCodeField,
  regionField,
  StreetField,
  streetField,
} from './fields';

export default OriginalReactComponent => {
  class WithAddressForm extends React.Component {
    constructor(props) {
      super(props);
      // We need to clear previous addressify results to not show wrong suggestions
      if (!isRunningContextCypress(window)) {
        cleanupAddresify();
      }

      this.streetSearchCallback = this.streetSearchCallback.bind(this);
      this.streetSearchCallbackOnFocus = this.streetSearchCallbackOnFocus.bind(this);
      this.getSuggestions = this.getSuggestions.bind(this);
      this.debouncedGetSuggestions = debounce(this.getSuggestions, 500).bind(this);
      this.getFieldsForRender = this.getFieldsForRender.bind(this);
      this.onChangeCallback = this.onChangeCallback.bind(this);
      this.onChangeFields = this.onChangeFields.bind(this);
      this.onBlurFields = this.onBlurFields.bind(this);
      this.prepareFields = this.prepareFields.bind(this);
      this.hasRegion = !!props.fields.region;

      this.state = this.prepareFields();
    }

    prepareFields() {
      const {
        fields,
        t,
        tMerchantAware,
        countries,
        usRegions,
        isMobile,
        language,
        onInit = () => {},
      } = this.props;

      const countryValue = get(fields.country, 'value', '');

      const shouldShowTooltips = language === 'en';

      const addressFields = formUtils.initForm({
        // FIXME: This "false" should be `isUnmanagedKyc`
        country: countryField(t, fields.country, countries, false),
        postalCode: postalCodeField(
          tMerchantAware,
          fields.postalCode,
          fields.country.value,
          shouldShowTooltips,
        ),
        street: streetField(
          t,
          fields.street,
          this.streetSearchCallback,
          this.streetSearchCallbackOnFocus,
          shouldShowTooltips,
        ),
        city: cityField(t, fields.city, { isMobile }),
        ...(this.hasRegion
          ? { region: regionField(t, fields.region, countryValue, usRegions, { isMobile }) }
          : {}),
      });

      onInit({ ...fields, ...addressFields });

      return {
        addressifyParams: { country: null, postalCode: null, street: null },
        addressFields,
        isCityAutofilled: false,
        isLoadingPrefillCity: false,
        isLoadingSuggestions: false,
        isRegionAutofilled: false,
        streetSuggestions: [],
        language,
      };
    }

    componentDidUpdate(prevProps, prevState) {
      const { language } = this.props;

      if (prevState.language !== language) {
        const state = this.prepareFields();
        /* eslint-disable-next-line react/no-did-update-set-state */
        this.setState({ ...state, language });
      }
    }

    streetSearchCallback(val) {
      this.getSuggestions('street', val);
    }

    streetSearchCallbackOnFocus() {
      const street = get(this.state, 'addressFields.street.value', '');
      if (street?.length) {
        this.streetSearchCallback(street);
      }
    }

    getSuggestions(key, val) {
      const { addressFields: fields } = this.state;

      if (isRunningContextCypress(window)) {
        return;
      }

      const { street, country, postalCode, city } = formUtils.getMappedValues(fields);

      const addressifyParams = {
        country,
        ...(!['country'].includes(key) && { postalCode }),
        ...(!['postalCode', 'country'].includes(key) && { street }),
      };

      if (key && val) {
        addressifyParams[key] = val;
      }

      addressifyParams.postalCode = addressifyParams.postalCode.replace(/\s/g, '').trim();

      if (
        country &&
        postalCode && // street is not mandatory
        !isEqual(addressifyParams, this.state.addressifyParams)
      ) {
        const loadingState = {};
        if (key === 'street') {
          loadingState.isLoadingSuggestions = true;
        }
        if (key === 'postalCode') {
          loadingState.isLoadingPrefillCity = true;
        }

        this.setState({ addressifyParams, ...loadingState });

        return this.props
          .getSuggestionsAddressify({ body: addressifyParams, region: this.props.merchantRegion })
          .then(
            ({
              data: {
                suggestions: {
                  addresses = [],
                  city: citySuggestion,
                  region: regionSuggestion,
                } = {},
                params: receivedParams,
              } = {},
            }) => {
              const loadingState = { isLoadingSuggestions: false, isLoadingPrefillCity: false };

              if (!isEqual(receivedParams, this.state.addressifyParams)) {
                return this.setState(loadingState);
              }

              const autoFilledState = {};

              if (citySuggestion && citySuggestion !== city) {
                this.onChangeFields('city', citySuggestion);
                autoFilledState.isCityAutofilled = true;
              }
              if (this.hasRegion && regionSuggestion && regionSuggestion !== city) {
                this.onChangeFields('region', regionSuggestion);
                autoFilledState.isRegionAutofilled = true;
              }

              return this.setState({
                streetSuggestions: addresses.map(({ street = '' } = {}) => ({
                  value: street,
                  label: street,
                })),
                ...loadingState,
                ...autoFilledState,
              });
            },
          )
          .catch(() => this.setState({ isLoadingPrefillCity: false, isLoadingSuggestions: false }));
      }
    }

    getFieldsForRender() {
      const {
        fields: {
          city: cityFromProps,
          country: countryFromProps,
          postalCode: postalCodeFromProps,
          region: regionFromProps,
          street: streetFromProps,
        },
        t,
        usRegions,
        isMobile,
      } = this.props;
      const {
        addressFields: {
          city: cityFromState,
          country: countryFromState,
          postalCode: postalCodeFromState,
          region: regionFromState,
          street: streetFromState,
        },
        isCityAutofilled,
        isLoadingPrefillCity,
        isLoadingSuggestions,
        isRegionAutofilled,
        streetSuggestions = [],
      } = this.state;

      const countryValue = get(countryFromState, 'value', '');
      const postalCodeValue = get(postalCodeFromState, 'value', '');

      const shouldShowStreetSuggestions =
        !isRunningContextCypress(window) && postalCodeValue.length > 0 && countryValue.length > 0;

      const postalCode = {
        ...postalCodeFromProps,
        ...set(postalCodeFromState, 'extraProps.loading', isLoadingPrefillCity),
        isInitialized: postalCodeFromProps.isInitialized,
        isValid: postalCodeFromState.isValid,
      };
      const city = {
        ...cityFromProps,
        ...set(cityFromState, 'extraProps.autofill', isCityAutofilled),
        isInitialized: cityFromProps.isInitialized,
        isValid: cityFromState.isValid,
      };
      const street = {
        ...streetFromProps,
        ...streetFromState,
        component: StreetField,
        isInitialized: streetFromProps.isInitialized,
        isValid: streetFromState.isValid,
        extraProps: {
          ...streetFromState.extraProps,
          isLoading: isLoadingSuggestions,
          options: streetSuggestions,
          showSuggestions: shouldShowStreetSuggestions,
        },
      };
      const country = {
        ...countryFromProps,
        ...countryFromState,
        isInitialized: countryFromProps.isInitialized,
        isValid: countryFromState.isValid,
      };

      let region = {};
      if (regionFromProps) {
        const newRegionField = regionField(t, regionFromState, countryValue, usRegions, {
          isMobile,
        });

        // if the country is US use data from props, validation from state is not being updated
        region = {
          region: {
            ...regionFromProps,
            ...set(newRegionField, 'extraProps.autofill', isRegionAutofilled),
            value: country.value === 'US' ? regionFromProps.value : regionFromState.value,
            errorMessage: regionFromProps.errorMessage || regionFromState.errorMessage,
            isInitialized: regionFromProps.isInitialized,
            isValid: country.value === 'US' ? regionFromProps.isValid : regionFromState.isValid,
            isOptional: country.value === 'US' ? regionFromProps.isOptional : true,
          },
        };
      }

      return {
        ...this.props.fields,
        country,
        postalCode,
        street,
        city,
        ...region,
      };
    }

    onChangeCallback(fields, key, val) {
      if (key === 'postalCode') {
        this.debouncedGetSuggestions(key, val);
      }
      this.setState({ addressFields: fields });
      this.props.onChange(key, val, { ...this.props.fields, ...fields });
    }

    onChangeFields(key, val) {
      const { addressFields: fields } = this.state;

      if (fields[key]) {
        if (key === 'city') {
          this.setState({ isCityAutofilled: false });
        }
        if (key === 'country') {
          const { tMerchantAware, language } = this.props;
          const shouldShowTooltips = language === 'en';
          fields.postalCode = postalCodeField(
            tMerchantAware,
            fields.postalCode,
            val,
            shouldShowTooltips,
          );
          fields.postalCode = formUtils.initForm({ postalCode: fields.postalCode }).postalCode;
        }

        /**
         * `formUtils.onChangeFields` calls the callback twice. We only get suggestions when the
         * first one is triggered. The second one gets triggered with 1 second of debounce after
         * first one and only sets `isInitialized`.
         */
        let isFirstCallback = true;
        formUtils.onChangeFields(key, val, { fields }, fields => {
          if (isFirstCallback) {
            this.onChangeCallback(fields, key, val);
            isFirstCallback = false;
          } else {
            this.setState({ addressFields: fields });
          }
        });
      } else {
        this.onChangeCallback(fields, key, val);
      }
    }

    onBlurFields(key) {
      const { onBlur = () => null, fields: fieldsFromProps } = this.props;
      const { addressFields: fields } = this.state;

      if (fields[key]) {
        formUtils.onBlurFields(key, { fields }, fields => {
          onBlur(key, { ...fieldsFromProps, ...fields });
        });
      } else {
        onBlur(key, { ...fieldsFromProps, ...fields });
      }
    }

    render() {
      const {
        countries,
        usRegions,
        cleanupAddresify,
        isUnmanagedKyc,
        t,
        getSuggestionsAddressify,
        ...otherProps
      } = this.props;

      return (
        <OriginalReactComponent
          {...otherProps}
          fields={this.getFieldsForRender()}
          onBlur={this.onBlurFields}
          onChange={this.onChangeFields}
        />
      );
    }
  }

  WithAddressForm.propTypes = {
    cleanupAddresify: PropTypes.func.isRequired,
    getSuggestionsAddressify: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    isUnmanagedKyc: PropTypes.bool.isRequired,
    language: PropTypes.string.isRequired,
    merchantRegion: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    tMerchantAware: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    countries: PropTypes.array,
    fields: PropTypes.object,
    isMobile: PropTypes.bool,
    name: PropTypes.string,
    postalCode: PropTypes.string,
    usRegions: PropTypes.array,
    onBlur: PropTypes.func,
    onInit: PropTypes.func,
  };

  return compose(
    clientComponents.withPromiseBasedCapabilities({
      getSuggestionsAddressify: CAPS.ADDRESIFY_SUGGESTIONS,
    }),
    connect(
      state => ({
        t: translator(state),
        countries: getCountries(state),
        isUnmanagedKyc: isUnmanagedKyc(state),
        language: i18nSelectors.language(state),
        merchantRegion: merchantRegion(state),
        usRegions: getUsStates(state),
        tMerchantAware: getMerchantAwareTranslator(state),
      }),
      { cleanupAddresify },
    ),
    withResponsive,
  )(WithAddressForm);
};
